import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Selector from '../../components/Selector/Selector';
import Accordion from '../../components/Accordion';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import {
  useDRSettlementForDrHistory,
  useDRSettlementAdditionalInfo,
} from '../../hooks/queries/drSettlementQueries';
import { useSetUpList } from '../../hooks/queries/setUpQueries';
import { ROUTES } from '../../utils/constants';
import './DRHistorySettlementDetail.css';


const formatStartEndTime = (startEndTime) => {
  if (!startEndTime) return '-';
  const digits = startEndTime.replace(/\D/g, '');
  if (digits.length !== 8) return startEndTime;
  const start = `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  const end = `${digits.slice(4, 6)}:${digits.slice(6, 8)}`;
  return `${start} ~ ${end}`;
};

const formatNumber = (value, { digits = 2, suffix = '' } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  const n = Number(value);
  const formatted = Number.isInteger(n)
    ? n.toLocaleString()
    : n.toLocaleString(undefined, { maximumFractionDigits: digits });
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const DRHistorySettlementDetail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;
  const targetDate = searchParams.get('targetDate') || '';
  const startEndTime = searchParams.get('startEndTime') || '';
  const selectedDrResourceSysId = searchParams.get('drResourceSysId') || '';

  const hasRequiredContext = !!targetDate && !!startEndTime;

  const {
    data: settlementRaw,
    isLoading: settlementLoading,
    isError: settlementIsError,
    error: settlementError,
  } = useDRSettlementForDrHistory({
    TargetDate: targetDate,
    StartEndTime: startEndTime,
  });

  const settlementItems = useMemo(() => {
    if (!settlementRaw) return [];
    if (Array.isArray(settlementRaw)) return settlementRaw;
    if (Array.isArray(settlementRaw.Items)) return settlementRaw.Items;
    if (Array.isArray(settlementRaw.responseList)) return settlementRaw.responseList;
    return [];
  }, [settlementRaw]);

  const drResourceOptions = useMemo(() => {
    return settlementItems
      .filter((item) => item?.DrResourceSysId)
      .map((item) => ({
        id: item.DrResourceSysId,
        label: item.DrResourceName || item.DrResourceSysId,
        data: item,
      }));
  }, [settlementItems]);

  useEffect(() => {
    if (!hasRequiredContext) return;
    if (drResourceOptions.length === 0) return;
    const exists = drResourceOptions.some((opt) => opt.id === selectedDrResourceSysId);
    if (!selectedDrResourceSysId || !exists) {
      const next = new URLSearchParams(searchParams);
      next.set('drResourceSysId', drResourceOptions[0].id);
      setSearchParams(next, { replace: true });
    }
  }, [drResourceOptions, selectedDrResourceSysId, hasRequiredContext, searchParams, setSearchParams]);

  const selectedOption = useMemo(() => {
    if (!selectedDrResourceSysId) return null;
    return drResourceOptions.find((opt) => opt.id === selectedDrResourceSysId) || null;
  }, [drResourceOptions, selectedDrResourceSysId]);

  const handleDrChange = (item) => {
    const next = new URLSearchParams(searchParams);
    if (item?.id) {
      next.set('drResourceSysId', item.id);
    } else {
      next.delete('drResourceSysId');
    }
    setSearchParams(next, { replace: true });
  };

  const selectedSettlement = selectedOption?.data || null;

  const reductionValue = useMemo(() => {
    if (!selectedSettlement) return null;
    const { UsageValue, CblValue } = selectedSettlement;
    if (UsageValue === undefined || UsageValue === null) return null;
    if (CblValue === undefined || CblValue === null) return null;
    return Number(CblValue) - Number(UsageValue);
  }, [selectedSettlement]);

  const settlementRange = useMemo(() => {
    if (!selectedSettlement) return null;
    const { MinSettlementAmount, MaxSettlementAmount } = selectedSettlement;
    if (
      (MinSettlementAmount === null || MinSettlementAmount === undefined) &&
      (MaxSettlementAmount === null || MaxSettlementAmount === undefined)
    ) {
      return null;
    }
    return { min: MinSettlementAmount, max: MaxSettlementAmount };
  }, [selectedSettlement]);

  const { data: additionalInfo } = useDRSettlementAdditionalInfo(
    selectedDrResourceSysId,
    {
      TargetRecordDate: targetDate,
      StartEndTime: startEndTime,
    }
  );

  const reasonForMissingValueAccordionItems = useMemo(() => {
    const raw = additionalInfo?.ReasonForMissingValue;
    if (raw === null || raw === undefined) return [];

    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        return [];
      }
    }
    if (!parsed || typeof parsed !== 'object') return [];

    return Object.entries(parsed).map(([title, messages], idx) => {
      const list = Array.isArray(messages) ? messages : [];
      return {
        id: `reason-${idx}`,
        title,
        count: list.length,
        content: list.length === 0 ? (
          <p className="text-b2-400 text-gray-600">해당 항목이 없습니다.</p>
        ) : (
          <div className="dr-history-settlement-detail__reason-list">
            {list.map((message, i) => (
              <p key={i} className="text-b2-400 text-base">
                {message}
              </p>
            ))}
          </div>
        ),
      };
    });
  }, [additionalInfo]);

  const handleBack = () => {
    navigate(ROUTES.DR_HISTORY);
  };

  return (
    <div className="page-container dr-history-settlement-detail-page">
      <div className='flex align-center mb-5'>
        <button className='btn icon-btn' onClick={handleBack} title="뒤로가기">
          <ChevronLeftIcon color='var(--gray-600)'/>
          <span className='text-b2-700 text-gray-600'>뒤로가기</span>
        </button>
      </div>

      {!hasRequiredContext ? (
        <div className="dr-history-settlement-detail__empty">
          <p className="text-b1-400 text-gray-600">
            대상 날짜 및 시간 정보가 없습니다. 이력 목록에서 다시 진입해주세요.
          </p>
        </div>
      ) : (
        <>
          <div className="dr-history-settlement-detail__context mb-10">
            <div className="dr-history-settlement-detail__context-item">
              <span className="text-b2-400 text-gray-600">대상 날짜</span>
              <span className="text-b2-700 text-base">
                {dayjs(targetDate).isValid() ? dayjs(targetDate).format('YYYY-MM-DD') : targetDate}
              </span>
            </div>
            <div className="dr-history-settlement-detail__context-item">
              <span className="text-b2-400 text-gray-600">발령 시간</span>
              <span className="text-b2-700 text-base">{formatStartEndTime(startEndTime)}</span>
            </div>
          </div>

          <div className="dr-history-settlement-detail__selector-row">
            <div className="dr-history-settlement-detail__selector-field">
              <label
                className="dr-history-settlement-detail__selector-label text-b2-700 text-gray-700"
                htmlFor="dr-history-settlement-detail-selector"
              >
                DR 자원 선택
              </label>
              <div id="dr-history-settlement-detail-selector">
                <Selector
                  mode="local"
                  options={drResourceOptions}
                  value={selectedOption}
                  onChange={handleDrChange}
                  placeholder={
                    settlementLoading
                      ? '정산 데이터 불러오는 중...'
                      : drResourceOptions.length === 0
                        ? '참여 DR 자원이 없습니다'
                        : 'DR 자원을 선택하세요'
                  }
                  disabled={settlementLoading || drResourceOptions.length === 0}
                  enableSearch
                  emptyMessage="DR 자원이 없습니다"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {settlementLoading ? (
            <div className="dr-history-settlement-detail__loading">
              <div className="loading-spinner" />
              <p className="text-b1-400 text-base">정산 데이터를 불러오는 중...</p>
            </div>
          ) : settlementIsError ? (
            <div className="dr-history-settlement-detail__error">
              <p className="text-b1-400 text-base">
                {settlementError?.message || '정산 데이터를 불러오지 못했습니다.'}
              </p>
            </div>
          ) : !selectedSettlement ? (
            <div className="dr-history-settlement-detail__empty">
              <p className="text-b2-400 text-gray-600">
                선택된 DR 자원의 정산 데이터가 없습니다.
              </p>
            </div>
          ) : (
            <>
            <section className="dr-history-settlement-detail__summary-grid">
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">
                  사용량
                </p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-primary">
                  {formatNumber(selectedSettlement.UsageValue)}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">
                  kWh
                </p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">
                  CBL 값
                </p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-primary">
                  {formatNumber(selectedSettlement.CblValue)}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">
                  kWh
                </p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">
                  감축량
                </p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-primary">
                  {formatNumber(reductionValue)}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">
                  kWh (CBL - 사용량)
                </p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">
                  예상 정산 금액
                </p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-primary">
                  {settlementRange
                    ? `${formatNumber(settlementRange.min)} ~ ${formatNumber(settlementRange.max)}`
                    : '-'}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">
                  원
                </p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">
                  알고리즘 / 버전
                </p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-base">
                  {selectedSettlement.AppliedCblAlgorithm || `(기본) ${defaultCblModuleName}`} / {selectedSettlement.AppliedCblAlgorithmVersion || `${defaultCblModuleVersion}`}
                </p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">총 고객계약번호 수</p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-base">
                  {additionalInfo?.TotalTargetPowerContractNumber || '-'}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">개</p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">사용된 고객계약번호 수</p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-success">
                  {additionalInfo?.TotalUsedValue || '-'}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">개</p>
              </div>
              <div className="dr-history-settlement-detail__summary-card">
                <p className="dr-history-settlement-detail__summary-card__label text-b2-400 text-gray-600">미사용 고객계약번호 수</p>
                <p className="dr-history-settlement-detail__summary-card__value text-h2 text-warning">
                  {additionalInfo?.TotalMissingValue || '-'}
                </p>
                <p className="dr-history-settlement-detail__summary-card__sub text-b2-400 text-gray-600">개</p>
                </div>
              </section>

              {reasonForMissingValueAccordionItems.length > 0 && (
                <section className="dr-history-settlement-detail__reason-section">
                  <h3 className="dr-history-settlement-detail__reason-section__title text-b1-700 text-base">
                    미사용 사유
                  </h3>
                  <Accordion
                    items={reasonForMissingValueAccordionItems}
                    defaultOpenIndex={-1}
                    className="dr-history-settlement-detail__reason-accordion"
                  />
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DRHistorySettlementDetail;
